// Internal absolute imports (@/ aliases)
// Lib utilities
import apiClient from '@/lib/api/client';

// Constants
import { API_ENDPOINTS } from '@/constants/api';

// Relative imports from same feature
import type {
  AuthResponse,
  ForgotPasswordRequest,
  LoginRequest,
  OtpVerifyRequest,
  OtpVerifyResponse,
  RegisterRequest,
  ResetPasswordRequest,
} from '../types';

/**
 * DOCU: Authentication service for handling login, registration, and logout operations. <br>
 * Triggered: Called by React Query mutation hooks (useLogin, useRegister, useLogout). <br>
 * Last Updated Date: December 1, 2025 <br>
 * @author Kirt, Updated by: Sam
 */
export const authService = {
  /**
   * DOCU: Authenticate user with email and password. <br>
   * Triggered: When user submits login form. <br>
   * Last Updated Date: December 1, 2025 <br>
   * @param data - Login credentials (email_address, password).
   * @returns Promise resolving to authentication response with user and tokens.
   * @author Kirt, Updated by: Sam
   */
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>(
      API_ENDPOINTS.LOGIN,
      data
    );
    return response.data;
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<{ code: string; result: { is_sent: boolean } }> => {
    const response = await apiClient.post<{ code: string; result: { is_sent: boolean } }>(
      API_ENDPOINTS.FORGOT_PASSWORD,
      data
    );
    return response.data;
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<{ code: string; result: { is_reset: boolean } }> => {
    const response = await apiClient.post<{ code: string; result: { is_reset: boolean } }>(
      API_ENDPOINTS.RESET_PASSWORD,
      data
    );
    return response.data;
  },

  verifyOtp: async (data: OtpVerifyRequest): Promise<OtpVerifyResponse> => {
    const response = await apiClient.post<OtpVerifyResponse>(
      API_ENDPOINTS.OTP_VERIFY,
      data
    );
    return response.data;
  },

  resendOtp: async (data: { otp_challenge_token: string }): Promise<{ code: string; result: { is_sent: boolean } }> => {
    const response = await apiClient.post<{ code: string; result: { is_sent: boolean } }>(
      API_ENDPOINTS.OTP_RESEND,
      data
    );
    return response.data;
  },

  /**
   * DOCU: Register new user account. <br>
   * Triggered: When user submits registration form. <br>
   * Last Updated Date: December 1, 2025 <br>
   * @param data - Registration data (email, name, password, role, etc.).
   * @returns Promise resolving to authentication response with user and tokens.
   * @author Kirt, Updated by: Sam
   */
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>(
      API_ENDPOINTS.REGISTER,
      data
    );
    return response.data;
  },

  /**
   * DOCU: Logout current user and invalidate session. <br>
   * Triggered: When user clicks logout button. <br>
   * Last Updated Date: December 1, 2025 <br>
   * @returns Promise resolving when logout completes (or fails silently).
   * @author Kirt, Updated by: Sam
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post(API_ENDPOINTS.LOGOUT);
    } catch (error) {
      // Even if logout fails on server, we still clear local state
      console.error('Logout error:', error);
    }
  },
};
